// BugReportModule.tsx
import React, { useState } from 'react';
import { Bug, X, Upload, Send, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { uploadService } from '../../lib/uploadService';
import { useAuth } from '../../hooks/useAuth';

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentModule: string;
}

const BugReportModal: React.FC<BugReportModalProps> = ({ isOpen, onClose, currentModule }) => {
  const { appUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file (reuse suggestion file validation)
    const validation = uploadService.validateSuggestionFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    setAttachmentFile(file);
    setError('');
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
  };

  const getModuleDisplayName = (module: string): string => {
    const moduleNames: { [key: string]: string } = {
      'home': 'Dashboard',
      'people': 'People Directory',
      'logos': 'Logos Module',
      'project-names': 'Project Names Module',
      'suggestion-box': 'Suggestion Box',
      'color-palettes': 'Color Palettes',
      'print-templates': 'Print Templates',
      'digital-templates': 'Digital Templates',
      'texts': 'Texts Module',
      'wiki': 'Wiki',
      'prompts': 'AI Prompts',
      'admin-users': 'User Management',
      'admin-skills': 'Skills Management',
      'admin-tags': 'Tag Management',
      'admin-whitelist': 'Whitelist Management',
      'profile': 'User Profile'
    };
    return moduleNames[module] || module;
  };

  const sendEmailNotification = async (bugData: any) => {
    try {
      const response = await fetch('https://us-central1-gule-sider.cloudfunctions.net/sendBugReport', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: bugData.title,
          description: bugData.description,
          module: currentModule,
          moduleDisplayName: getModuleDisplayName(currentModule),
          userEmail: appUser?.email,
          userName: appUser?.displayName,
          attachmentUrl: bugData.attachmentUrl || '',
          hasAttachment: !!bugData.attachmentUrl,
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send bug report email');
      }

      const result = await response.json();
      console.log('Bug report sent successfully:', result);
      return { success: true };
      
    } catch (error: any) {
      console.error('Failed to send bug report:', error);
      throw new Error(error.message || 'Failed to send bug report');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.description.trim()) {
      setError('Title and description are required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      let attachmentUrl = '';
      
      // Upload attachment if present
      if (attachmentFile) {
        const uploadResult = await uploadService.uploadSuggestionFile(attachmentFile);
        attachmentUrl = uploadResult.url;
      }

      // Prepare bug report data
      const bugReportData = {
        ...formData,
        module: currentModule,
        moduleDisplayName: getModuleDisplayName(currentModule),
        userEmail: appUser?.email,
        userName: appUser?.displayName,
        attachmentUrl,
        hasAttachment: !!attachmentFile,
        timestamp: new Date().toISOString()
      };

      // Send email notification
      await sendEmailNotification(bugReportData);

      setSubmitted(true);
      
      // Reset form after short delay
      setTimeout(() => {
        setFormData({
          title: '',
          description: ''
        });
        setAttachmentFile(null);
        setSubmitted(false);
        onClose();
      }, 2000);

    } catch (error: any) {
      setError(error.message || 'Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setFormData({
      title: '',
      description: ''
    });
    setAttachmentFile(null);
    setError('');
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Bug className="text-red-500" size={24} />
              <div>
                <h2 className="text-xl font-semibold">Report a Bug</h2>
                <p className="text-sm text-gray-600">
                  Module: {getModuleDisplayName(currentModule)}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Success State */}
          {submitted && (
            <div className="text-center py-8">
              <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-green-800 mb-2">
                Bug Report Submitted!
              </h3>
              <p className="text-green-700">
                Thank you for helping us improve Gule Sider. 
                Jon has been notified and will review your report.
              </p>
            </div>
          )}

          {/* Form */}
          {!submitted && (
            <>
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Bug Title */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Bug Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full p-3 border rounded-lg"
                    placeholder="Brief description of the issue"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Description *
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full p-3 border rounded-lg h-32"
                    placeholder="Describe what happened and what you were trying to do when the bug occurred"
                    required
                  />
                </div>

                {/* File Attachment */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Screenshot or File
                  </label>
                  <div className="space-y-3">
                    {!attachmentFile ? (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600 mb-2">
                          Attach a screenshot or relevant file
                        </p>
                        <input
                          type="file"
                          accept="image/*,.pdf,.doc,.docx,.txt"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="bug-attachment"
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById('bug-attachment')?.click()}
                          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Choose File
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          Images, PDFs, or documents up to 10MB
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-2">
                          <Upload size={16} className="text-gray-500" />
                          <span className="text-sm font-medium">{attachmentFile.name}</span>
                          <span className="text-xs text-gray-500">
                            ({(attachmentFile.size / 1024 / 1024).toFixed(1)} MB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={removeAttachment}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={submitting}
                    className="flex-1 px-4 py-3 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!formData.title.trim() || !formData.description.trim() || submitting}
                    className="flex-1 bg-red-500 text-white px-4 py-3 rounded-lg border border-red-500 flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-red-600"
                  >
                    {submitting ? (
                      <>
                        <Loader className="animate-spin" size={16} />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Submit Bug Report
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const BugReportModule: React.FC = () => {
  return (
    <div className="bg-white p-8 rounded-lg border shadow-sm">
      <h2 className="text-xl font-semibold mb-4">Bug Reports</h2>
      <p className="text-muted-foreground">
        Bug reporting is handled through the floating bug report button. 
        This ensures you can report issues from any module you're currently using.
      </p>
    </div>
  );
};

export { BugReportModal };